import {
  breakAfter,
  layOut,
  MINUTE,
  planTimerStart,
  SEAT_MINUTES,
  SessionMove,
  SessionPiece,
  SessionRemoval,
  SHORTEST_COUNTDOWN_MINUTES,
  StartWay,
  TimerBreaks,
  TimerCalendarItem,
  TimerStartOption
} from './timerStart';

/**
 * Bumping the day down (Petr, 2026-10-18): a session put somewhere new bumps the study sessions it touches further down,
 * in order, around classes, events and commutes, which never move; like bumpers, until there is enough study time.
 * Used by ▶ on a late session (planBump), Keep going (planAfterOvertime) and Bumpers drags (planBumpers); bumpAfter
 * knows nothing about the timer.
 *
 * A session that has to move goes to the first room after where it was, trying in turn:
 * 1. Whole, with the breaks the planning settings want before and after it.
 * 2. Whole, with the breaks shrunk (none between sessions, 5 minutes before an event, none before a commute), any room
 *    left given back to the break before it.
 * 3. Split: what fits fills the room, and the rest goes on to the next room. A piece keeps the breaks around it (breaks
 *    shrink only to keep a session whole) and is at least 15 minutes (Petr, 2026-10-19: a full day's 5-minute breaks
 *    were filled with 5-minute scraps).
 * Room counts only inside a study block (the stretch between classes, events, commutes and bedtime, within awake time)
 * at least the term's minimum study block long; a piece in it may be shorter than that (Petr: 40m here and 20m in the
 * next).
 * Past bedtime it goes on into free room on the next days, never bumping their sessions, and never past the task's due
 * date: with no room before it the session is shortened, or taken off, its minutes back to the task. A session whose
 * place is still free stays, which ends the chain. With Bumpers on (Petr, 2026-10-29: the same as a drag), ▶ and Keep
 * going chain into the next days as a drag does instead: a day's spillover starts the next day's study time and bumps
 * that day's sessions, and a session that would lose time to its due date stays where it is.
 */

// The smallest piece a session is split into: the length of Auto-plan's shortest (review) sessions.
export const PIECE_FLOOR_MINUTES = 15;
const DAY = 24 * 60 * MINUTE;

type Interval = { start: number; end: number };
type Taken = Interval & { kind: TimerCalendarItem['kind'] | 'started'; minutes: number };

export type BumpContext = {
  /** Awake windows from the start of today to the horizon. */
  awake: Interval[];
  /** When today ends, the next 4am (the calendar's days run 4am to 4am): past it is "a later day". */
  dayEnd: number;
  /** How far ahead a session may go. */
  horizonEnd: number;
  /** The term's minimum study block, in minutes. */
  minBlock: number;
  /**
   * When each day ends, from today to the horizon (Bumpers): a session bumped on a later day first looks for room for
   * it whole on that day. Without them every day is "today" (▶, whose chain stays on today).
   */
  dayEnds?: number[];
  /** When today began on the calendar (Bumpers: a drop over the past on an earlier day is the plain drag). */
  dayStart?: number;
};

export type BumpResult = { moves: SessionMove[]; creates: SessionPiece[]; removes: SessionRemoval[] };

const minutesBetween = (start: number, end: number) => Math.round((end - start) / MINUTE);

/** Which day `at` is in, counted from today (0); without the days, today or later (1). */
const dayIndexOf = (at: number, context: BumpContext) => {
  const ends = context.dayEnds ?? [context.dayEnd];
  const index = ends.findIndex(end => end > at);
  return index < 0 ? ends.length : index;
};

/** The sessions a bump shortened or took off. */
const lostIn = (result: BumpResult) => [
  ...result.removes.map(gone => gone.id),
  ...result.moves.filter(move => move.shortenedBy).map(move => move.id)
];

/** A session due after now and before the horizon, which a chain can hold to its due date. */
const dueWithin = (item: TimerCalendarItem, now: number, horizonEnd: number) =>
  !!item.dueDate && item.dueDate > now && item.dueDate < horizonEnd;

/**
 * Held by their due dates until nothing more is: each round holds the first of `holdable` that a bump would shorten or
 * take off, as if it were a class, and bumps again (one at a time, as holding it can give the ones after it room
 * again), so nothing lands after its due date and nothing is cut for one. `done` ends it early.
 */
const holdForDueDates = <T extends { lost: string[] }>(
  attempt: (held: Set<string>) => T,
  holdable: TimerCalendarItem[],
  done: (tried: T) => boolean = () => false
): T & { held: Set<string> } => {
  const held = new Set<string>();
  const inOrder = [...holdable].sort((a, b) => a.start - b.start);
  for (;;) {
    const tried = attempt(held);
    const due = inOrder.find(item => tried.lost.includes(item.id) && !held.has(item.id));
    if (!due || done(tried)) return { ...tried, held };
    held.add(due.id);
  }
};

/** The sessions sent on to a later day than their own, and when the first of them goes there; for the line's "1 to Mon". */
const laterOf = (result: BumpResult, queue: TimerCalendarItem[], context: BumpContext) => {
  const dayWas = new Map(queue.map(item => [item.id, dayIndexOf(item.start, context)]));
  const laterStarts = new Map<string, number>();
  [
    ...result.moves.map(move => ({ id: move.id, start: move.start })),
    ...result.creates.map(piece => ({ id: piece.fromId, start: piece.start }))
  ]
    .filter(piece => dayIndexOf(piece.start, context) > (dayWas.get(piece.id) ?? 0))
    .forEach(piece => laterStarts.set(piece.id, Math.min(piece.start, laterStarts.get(piece.id) ?? Infinity)));
  return laterStarts.size ? { count: laterStarts.size, at: Math.min(...Array.from(laterStarts.values())) } : undefined;
};

/**
 * `from` with every interval in `cut` taken out, in time order. Walks the cuts in time order rather than trying each on
 * every interval: this gets a whole term's awake time and classes, several times at each step of a drag.
 */
export const subtract = (from: Interval[], cut: Interval[]): Interval[] => {
  const cuts = [...cut].sort((a, b) => a.start - b.start);
  const left: Interval[] = [];
  let first = 0;
  let previousStart = -Infinity;
  for (const interval of from) {
    // Cuts over before this interval needn't be looked at again, as long as the intervals come in time order.
    if (interval.start < previousStart) first = 0;
    previousStart = interval.start;
    while (first < cuts.length && cuts[first].end <= interval.start) first++;
    let at = interval.start;
    for (let i = first; i < cuts.length && cuts[i].start < interval.end; i++) {
      if (cuts[i].start > at) left.push({ start: at, end: cuts[i].start });
      at = Math.max(at, cuts[i].end);
    }
    if (at < interval.end) left.push({ start: at, end: interval.end });
  }
  return left.sort((a, b) => a.start - b.start);
};

/** The break wanted after something taken, in minutes. */
const wantedAfter = (taken: Taken | undefined, breaks: TimerBreaks) => {
  if (!taken) return 0;
  if (taken.kind === 'fixed') return breaks.eventBreakMinutes ?? 0;
  if (taken.kind === 'commute') return 0;
  return breakAfter(taken.minutes, breaks);
};

/** The breaks before something taken, after a session `minutes` long: as wanted, and as short as they may go. */
const breakBefore = (taken: Taken | undefined, minutes: number, breaks: TimerBreaks) => {
  if (!taken) return { wanted: 0, least: 0 };
  return {
    wanted: breakAfter(minutes, breaks),
    least: taken.kind === 'fixed' ? SEAT_MINUTES : 0
  };
};

/**
 * Places `queue` (study sessions, in order) round what is `taken`, each no earlier than where it was nor than `from`.
 * Sessions whose place is still free stay. Returns the moves (a session's first piece), the pieces after it, and the
 * sessions taken off.
 */
export const bumpAfter = ({
  queue,
  taken,
  from,
  now,
  breaks,
  context,
  endsWhenClear
}: {
  queue: TimerCalendarItem[];
  taken: Taken[];
  from: number;
  now: number;
  breaks: TimerBreaks;
  context: BumpContext;
  /**
   * Where what was put there ends (Bumpers): once a session starts past it and past everything moved, the chain is over
   * and the rest stay, so an overlap the student left days away isn't tidied up by a drag today.
   */
  endsWhenClear?: number;
}): BumpResult => {
  const placed: Taken[] = [...taken];
  // Study blocks: awake time less what is fixed; free time only counts in one at least the minimum study block long.
  const blocks = subtract(
    context.awake,
    taken.filter(item => item.kind === 'fixed' || item.kind === 'commute')
  ).filter(block => minutesBetween(block.start, block.end) >= context.minBlock);

  const result: BumpResult = { moves: [], creates: [], removes: [] };
  const endingAt = (at: number) => placed.find(item => Math.abs(item.end - at) < MINUTE);
  const startingAt = (at: number) => placed.find(item => Math.abs(item.start - at) < MINUTE);
  // A session clashes with what's taken if they overlap, or it ends less than 5 minutes before an event. Only a clash
  // moves a session: one the student put in a short gap between classes stays put unless something lands on it.
  const clashes = (start: number, end: number) =>
    placed.some(item => {
      const after = item.kind === 'fixed' && item.start >= end ? SEAT_MINUTES * MINUTE : 0;
      return start < item.end && end + after > item.start;
    });

  // Where the last session moved ended: the next one moved goes after it, so the day keeps its order (and a break left
  // between two moved sessions isn't filled with a scrap of a third).
  let cursor = from;
  // Only what reaches into `at`–`limit` counts, which keeps a chain over a whole term quick; what ends at `at` or starts
  // at `limit` still bounds a gap there.
  const gapsAfter = (at: number, limit: number) =>
    subtract(
      blocks.filter(block => block.end > at && block.start < limit),
      placed.filter(item => item.end >= at && item.start <= limit)
    ).filter(free => free.end > at && Math.max(free.start, at) < limit);
  // The end of the day `at` is in: today's without the days (▶), else that day's.
  const dayEndAfter = (at: number) => context.dayEnds?.find(end => end > at) ?? context.dayEnd;
  // How far what this bump placed reaches; past it, with endsWhenClear, nothing more moves.
  let reach = endsWhenClear ?? Infinity;
  // How a gap is bounded: the break wanted after what comes before it, and the breaks before what comes after it.
  const bounds = (gap: Interval, at: number, limit: number, minutes: number) => {
    const start = Math.max(gap.start, at);
    const before = start === gap.start ? endingAt(gap.start) : undefined;
    const next = gap.end <= limit ? startingAt(gap.end) : undefined;
    return {
      start,
      end: Math.min(gap.end, limit),
      wantedFirst: wantedAfter(before, breaks) * MINUTE,
      ...breakBefore(next, minutes, breaks)
    };
  };
  // A session `length` long, whole in this gap: 1. with the breaks wanted, 2. (`shrink`) with them shrunk, any room left
  // given back to the break before it. None if it doesn't fit.
  const whole = (gap: Interval, at: number, limit: number, length: number, shrink = true): Interval | undefined => {
    const { start, end, wantedFirst, wanted, least } = bounds(gap, at, limit, Math.round(length / MINUTE));
    if (start + wantedFirst + length <= end - wanted * MINUTE)
      return { start: start + wantedFirst, end: start + wantedFirst + length };
    if (!shrink) return undefined;
    const room = end - least * MINUTE - start;
    if (length > room) return undefined;
    const back = Math.min(wantedFirst, room - length);
    return { start: start + back, end: start + back + length };
  };
  const take = (piece: Interval) => {
    placed.push({ ...piece, kind: 'session', minutes: Math.round((piece.end - piece.start) / MINUTE) });
    if (endsWhenClear !== undefined) reach = Math.max(reach, piece.end);
  };

  for (const session of queue) {
    if (session.start >= reach) break;
    const length = session.end - session.start;
    const minutes = minutesBetween(session.start, session.end);
    // Still free where it is (and not in the past): it stays, and so may everything after it.
    if (session.start >= from && !clashes(session.start, session.end)) {
      placed.push({ start: session.start, end: session.end, kind: 'session', minutes });
      continue;
    }

    const due = session.dueDate && session.dueDate > now ? session.dueDate : Infinity;
    const limit = Math.min(due, context.horizonEnd);
    const searchFrom = Math.max(session.start, from, cursor);
    const pieces: Interval[] = [];
    let remaining = length;

    // Whole, in the first room today (Bumpers: on the day it's pushed along in) that takes it: 1. with the breaks
    // wanted, 2. with them shrunk, any room left given back to the break before it.
    for (const gap of gapsAfter(searchFrom, Math.min(limit, dayEndAfter(searchFrom)))) {
      const fit = whole(gap, searchFrom, limit, length);
      if (fit) {
        pieces.push(fit);
        break;
      }
    }
    if (pieces.length) {
      remaining = 0;
      take(pieces[0]);
    } else {
      // 3. No room today for it whole: split, each room filled in turn (into the next days past bedtime), each piece
      // with the breaks around it as wanted and none under the floor, until it is all placed or its due date (or the
      // horizon) comes.
      const floor = PIECE_FLOOR_MINUTES * MINUTE;
      let at = searchFrom;
      for (const gap of gapsAfter(at, limit)) {
        if (remaining <= 0) break;
        // What's left fits whole here, its breaks as wanted.
        const rest = whole(gap, at, limit, remaining, false);
        if (rest) {
          pieces.push(rest);
          take(rest);
          remaining = 0;
          break;
        }
        const { start: gapStart, end, wantedFirst, wanted } = bounds(gap, at, limit, Math.round(remaining / MINUTE));
        const start = gapStart + wantedFirst;
        const room = end - wanted * MINUTE - start;
        const piece = Math.min(remaining, remaining - room < floor && remaining > room ? remaining - floor : room);
        if (piece >= floor) {
          const placedPiece = { start, end: start + piece };
          pieces.push(placedPiece);
          take(placedPiece);
          remaining -= piece;
        }
        at = gap.end;
      }
    }

    if (!pieces.length) {
      result.removes.push({ id: session.id, title: session.title, minutes });
      continue;
    }
    cursor = Math.max(cursor, pieces[pieces.length - 1].end);
    const [first, ...rest] = pieces;
    const shortenedBy = Math.round(remaining / MINUTE);
    result.moves.push({
      id: session.id,
      start: first.start,
      end: first.end,
      title: session.title,
      by: Math.round((first.start - session.start) / MINUTE),
      ...(shortenedBy > 0 ? { shortenedBy } : {})
    });
    rest.forEach(piece =>
      result.creates.push({ fromId: session.id, start: piece.start, end: piece.end, title: session.title })
    );
  }
  return result;
};

/**
 * Bedtime tonight: the end of the awake time `now` is in, to the minute (awake until 23:59:59 is bedtime at 00:00).
 * None outside awake time.
 */
export const bedtimeAt = (now: number, awake: Interval[]) => {
  const window = awake.find(interval => interval.start <= now && now < interval.end);
  return window ? Math.round(window.end / MINUTE) * MINUTE : undefined;
};

type StartInput = {
  now: number;
  plannedMinutes: number;
  /** Where the started session was planned: today's missed sessions after it come along. */
  startedFrom: number;
  /** The calendar's other blocks (the started session left out). */
  items: TimerCalendarItem[];
  /** The started session as the calendar has it (its task and due date), for the rest of it cut by a class. */
  started?: TimerCalendarItem;
  breaks: TimerBreaks;
  context: BumpContext;
  /**
   * Bumpers on (Petr, 2026-10-29): the chain carries on into the next days as a drag's does, `context` reaching the
   * term's end with its `dayEnds`, and sessions held to their due dates rather than cut.
   */
  chain?: boolean;
};

const isFixed = (item: TimerCalendarItem) => item.kind !== 'session';
const firstFixedAfter = (items: TimerCalendarItem[], at: number) =>
  items.filter(item => isFixed(item) && item.start >= at).sort((a, b) => a.start - b.start)[0];
// A session held to its due date is passed up to its start; a class keeps 5 minutes before it, a commute none.
const leastBeforeFixed = (item: TimerCalendarItem) =>
  item.kind === 'commute' || item.kind === 'session' ? 0 : SEAT_MINUTES;

/**
 * ▶ on a session: its block from now, whole unless a class, event or commute starts first (then stopping before it,
 * as always), and today's untouched sessions bumped down behind it, in order: the one under way, the ones to come, and
 * the missed ones planned after it (catching up, Petr 2026-10-18). With `chain` (Bumpers on), on into the next days, a
 * session the chain would push past its due date staying where it is; one that starts while this block would run
 * stops it there, as a class does.
 * What a stop like that cuts off the started session goes on (Petr, 2026-10-29: "schedule whatever is left into the
 * next available study block and push things down if need be"): first in the queue, as a session of its own, so it
 * lands in the next study block with room, bumping what's there, and is split or kept to its due date as any bumped
 * session is (`rest`). Under 15 minutes, or stopping at bedtime (a Change pick), it goes back to the task.
 * The student's own Activities give way (Petr, 2026-10-29): with under 15 minutes of study before one, or one already
 * under way, ▶ runs through it and says so (`runsThrough`; `stopBefore`, a Change pick, stops for it anyway). Classes,
 * commutes, own and imported events always stop it, however little time is left, the seat time shrinking first.
 */
export const planBump = ({
  now,
  plannedMinutes,
  startedFrom,
  items,
  started,
  breaks,
  context,
  stopAt,
  stopBefore,
  chain = false
}: StartInput & { stopAt?: number; stopBefore?: string }): TimerStartOption => {
  const plan = Math.max(0, Math.round(plannedMinutes));
  const dayStart = context.dayEnd - DAY;
  const fixed = firstFixedAfter(items, now);

  const bumps = (item: TimerCalendarItem) =>
    item.kind === 'session' &&
    item.movable !== false &&
    item.start >= dayStart &&
    (chain || item.start < context.dayEnd) &&
    (item.end > now || item.start >= startedFrom);
  const underWay = items.find(item => item.flexible && item.start < now && item.end > now);
  const attempt = (held: Set<string>) => {
    // What it stops for: the first thing ahead that won't move (a class, event or commute, or a session held to its
    // due date) that it would run into, past the Activities it runs through.
    let minutes = plan;
    let ahead: TimerCalendarItem | undefined;
    let through = underWay;
    const inOrder = items
      .filter(item => (isFixed(item) || held.has(item.id)) && item.start >= now)
      .sort((a, b) => a.start - b.start);
    // What it runs through, already under way or passed by: the commute just after one is no trip to make (Petr,
    // 2026-10-29: ▶ in a class stopped for the commute home from it).
    const passed = items.filter(item => item.kind === 'fixed' && item.start < now && item.end > now);
    const leavesPassed = (item: TimerCalendarItem) =>
      item.kind === 'commute' && passed.some(gone => Math.abs(gone.end - item.start) < MINUTE);
    for (const item of inOrder) {
      if (now + (plan + leastBeforeFixed(item)) * MINUTE <= item.start) break;
      if (leavesPassed(item)) continue;
      const until = Math.floor((item.start - now) / MINUTE);
      const fit = until - leastBeforeFixed(item);
      if (item.flexible && item.id !== stopBefore && fit < PIECE_FLOOR_MINUTES) {
        through = through ?? item;
        passed.push(item);
        continue;
      }
      // No time to get to the seat: up to its start; starting this minute, it is as good as under way.
      const study = fit >= 1 ? fit : until;
      if (study < 1) {
        passed.push(item);
        continue;
      }
      minutes = study;
      ahead = item;
      break;
    }
    // Stopping at bedtime (a Change pick): no further than it.
    if (stopAt !== undefined) minutes = Math.min(minutes, Math.max(1, Math.floor((stopAt - now) / MINUTE)));
    const stopFor =
      minutes < plan && ahead ? { title: ahead.title, at: ahead.start, commute: ahead.kind === 'commute' } : undefined;
    // An Activity it runs through, said only when the block reaches it.
    const runsThrough = through && through.start < now + minutes * MINUTE ? through : undefined;
    const moving = (item: TimerCalendarItem) => bumps(item) && !held.has(item.id);
    // The rest, as if it began with this block: clashing with it, it is placed first, after it.
    const cut = started && stopAt === undefined ? plan - minutes : 0;
    const leftover: TimerCalendarItem[] =
      started && cut >= PIECE_FLOOR_MINUTES
        ? [{ ...started, kind: 'session', movable: true, start: now, end: now + cut * MINUTE }]
        : [];
    const queue = [...leftover, ...items.filter(moving).sort((a, b) => a.start - b.start)];
    const end = now + minutes * MINUTE;
    const taken: Taken[] = [
      ...items
        .filter(item => !moving(item))
        .map(item => ({ start: item.start, end: item.end, kind: item.kind, minutes: minutesBetween(item.start, item.end) })),
      { start: now, end, kind: 'started', minutes }
    ];
    // With the chain, once it's clear of what this start placed, the rest stay, as after a drag.
    const result = bumpAfter({ queue, taken, from: now, now, breaks, context, endsWhenClear: chain ? end : undefined });
    return { minutes, stopFor, runsThrough, queue, result, lost: lostIn(result) };
  };
  // Held only from a few minutes ahead: one already under way can't be, and one starting this minute would leave the
  // started block nothing.
  const tried = chain
    ? holdForDueDates(
        attempt,
        items.filter(
          item =>
            bumps(item) &&
            dueWithin(item, now, context.horizonEnd) &&
            item.start >= now + SHORTEST_COUNTDOWN_MINUTES * MINUTE
        )
      )
    : attempt(new Set());
  const { minutes, stopFor, runsThrough, queue } = tried;
  // The rest of the started session apart from the others: new sessions of it, its first piece too.
  const own = started?.id;
  const restMove = tried.result.moves.find(move => move.id === own);
  const rest: SessionPiece[] = [
    ...(restMove ? [{ fromId: restMove.id, start: restMove.start, end: restMove.end, title: restMove.title }] : []),
    ...tried.result.creates.filter(piece => piece.fromId === own)
  ];
  const others: BumpResult = {
    moves: tried.result.moves.filter(move => move.id !== own),
    creates: tried.result.creates.filter(piece => piece.fromId !== own),
    removes: tried.result.removes.filter(gone => gone.id !== own)
  };
  const { moves, creates, removes } = others;
  const restMinutes = rest.reduce((sum, piece) => sum + minutesBetween(piece.start, piece.end), 0);
  const base = {
    way: 'bump' as StartWay,
    minutes,
    givesBack: plan - minutes - restMinutes,
    stopFor,
    ...(rest.length ? { rest } : {}),
    ...(runsThrough ? { runsThrough: { id: runsThrough.id, title: runsThrough.title, at: runsThrough.start } } : {})
  };
  if (!moves.length && !creates.length && !removes.length && !rest.length) {
    return { ...base, kind: stopFor ? 'stopsFor' : 'fits', moves: [] };
  }

  const later = laterOf(others, queue, context);
  // Only pushed along, whole, before the next fixed thing: the familiar "Pushes the next session by 25m".
  const simple =
    !stopFor &&
    !creates.length &&
    !removes.length &&
    moves.every(move => !move.shortenedBy && move.start < context.dayEnd && (!fixed || move.end <= fixed.start));
  return {
    ...base,
    kind: simple ? 'pushes' : 'bumps',
    moves,
    creates,
    removes,
    later
  };
};

/**
 * Add time on a session already logged (Countdown, timer spec "Countdown adds time"): the block grows by
 * `extendMinutes` from where its logged time ends, and it goes as ▶ goes (Petr, 2026-10-29: "same as ▶"): the sessions
 * after it bumped down in order, the added time stopping for a class with the rest of it going on, Activities run
 * through under 15 minutes, on into the next days with Bumpers on. Planned from now when the block ended earlier, so
 * nothing lands in the past; nothing missed before comes along.
 */
export const planAddTime = ({
  blockEnd,
  now,
  extendMinutes,
  ...start
}: Omit<StartInput, 'now' | 'plannedMinutes' | 'startedFrom'> & {
  blockEnd: number;
  now: number;
  extendMinutes: number;
}): TimerStartOption => {
  const from = Math.max(blockEnd, now);
  return planBump({ ...start, now: from, plannedMinutes: extendMinutes, startedFrom: from });
};

export type OvertimePlan = BumpResult & { later?: { count: number; at: number } };

/**
 * Keep going, then ✓ (timer spec "Keep going"), or more time logged on a session than its block held: the block now
 * ends at `blockEnd`, and the study sessions it runs into are bumped down behind it as ▶ bumps them (Petr, 2026-10-29):
 * in order, the ones it ran over catching up, today's only, or with `chain` (Bumpers on) on into the next days, sessions
 * held to their due dates. Nothing moves while the timer runs; this is once, at ✓.
 */
export const planAfterOvertime = ({
  blockStart,
  blockEnd,
  now,
  items,
  breaks,
  context,
  chain = false
}: {
  blockStart: number;
  blockEnd: number;
  now: number;
  /** The calendar's other blocks (the session itself left out). */
  items: TimerCalendarItem[];
  breaks: TimerBreaks;
  context: BumpContext;
  chain?: boolean;
}): OvertimePlan => {
  // Not into time already gone, should ✓ come a while after the time stopped.
  const from = Math.max(blockEnd, now);
  const dayStart = context.dayEnd - DAY;
  // The ones it ran over, and those still to come that the chain may reach; not one it never touched, missed or under
  // way (more time typed on this morning's session leaves the afternoon's alone).
  const bumps = (item: TimerCalendarItem) =>
    item.kind === 'session' &&
    item.movable !== false &&
    item.start >= dayStart &&
    (chain || item.start < context.dayEnd) &&
    (item.start >= from || (item.start < blockEnd && item.end > blockStart));
  const attempt = (held: Set<string>) => {
    const moving = (item: TimerCalendarItem) => bumps(item) && !held.has(item.id);
    const queue = items.filter(moving).sort((a, b) => a.start - b.start);
    const taken: Taken[] = [
      ...items
        .filter(item => !moving(item))
        .map(item => ({ start: item.start, end: item.end, kind: item.kind, minutes: minutesBetween(item.start, item.end) })),
      { start: blockStart, end: blockEnd, kind: 'started', minutes: minutesBetween(blockStart, blockEnd) }
    ];
    const result = bumpAfter({ queue, taken, from, now, breaks, context, endsWhenClear: chain ? from : undefined });
    return { queue, result, lost: lostIn(result) };
  };
  const tried = chain
    ? holdForDueDates(
        attempt,
        items.filter(item => bumps(item) && dueWithin(item, now, context.horizonEnd) && item.start >= from)
      )
    : attempt(new Set());
  return { ...tried.result, later: laterOf(tried.result, tried.queue, context) };
};

export type BumpersPlan = {
  /** fits: nothing else moves; bumps: sessions move; shortened: the dragged one shrinks to fit the term; refused: no
   *  room for even the shortest piece, the drop springs back. */
  kind: 'fits' | 'bumps' | 'shortened' | 'refused';
  /** Where the dragged session goes, and how long it is there. */
  start: number;
  end: number;
  minutes: number;
  /** Minutes it lost to fit (shortened). */
  shortenedBy?: number;
  moves: SessionMove[];
  creates: SessionPiece[];
  /** Sessions kept where they are by their due dates, the chain flowing round them. */
  held: string[];
  /** The start of the first and the last day anything moved to, for the tag's "some go past Sat 31". */
  firstDay?: number;
  lastDay?: number;
};

/**
 * Bumpers (decided with Petr 2026-10-19, timer spec "Bumpers: dragging bumps the chain"): a session dropped (or
 * stretched) on the calendar takes that spot, and the study sessions it touches move down in order, as with ▶. Unlike
 * ▶, the chain carries on into the next days: a day's spillover starts the next day's study time and pushes that day's
 * sessions down, until a free gap takes it, as far as the term's end.
 *
 * - The dragged session stays whole. Dropped on (or partly on) a class, event, commute or a session that can't move,
 *   it goes right after it (after a class, with the break wanted after one); it may end as a class starts.
 * - A session the chain would push past its due date stays where it is, as if it were a class, and the chain flows round
 *   it: nothing lands after its due date, nothing is cut for one.
 * - Planned sessions never lose time to a drag: if even the term can't take the chain, the dragged session shrinks to
 *   what fits, in 5-minute steps, no shorter than 15 minutes (stretched, no shorter than it was); else it's refused.
 *
 * `items` are the calendar's blocks without the dragged session; `context` reaches the term's end, with `dayEnds`.
 */
export const planBumpers = ({
  now,
  start,
  minutes,
  stretchedFrom,
  items,
  breaks,
  context,
  id,
  carry = false,
  wholeOnly = false
}: {
  now: number;
  /** Where it was dropped, or (stretched) where it starts. */
  start: number;
  minutes: number;
  /** Stretched by its bottom edge from this many minutes: it keeps its start. */
  stretchedFrom?: number;
  items: TimerCalendarItem[];
  breaks: TimerBreaks;
  context: BumpContext;
  /** The dragged session, should it still be among the items. */
  id?: string;
  /** Bump: every session it's dragged past travels with it, not only the missed ones. */
  carry?: boolean;
  /** Refused rather than shortened when it doesn't fit whole (planBumpersEarlier, which then bumps later instead). */
  wholeOnly?: boolean;
}): BumpersPlan => {
  const others = items.filter(item => item.id !== id);
  const stays = (item: TimerCalendarItem) => item.kind !== 'session' || item.movable === false;
  // Sessions it's dragged past, between where it was and where it goes, come along after it: the missed ones always
  // (Petr, 2026-10-28: "I missed three sessions … I drag the first to now … the two I missed would be bumped as well"),
  // the others with Bump (`carry`). Jump leaves those where they are.
  const was = id !== undefined ? items.find(item => item.id === id) : undefined;
  const passedOver = (item: TimerCalendarItem, at: number) =>
    !!was && item.end <= at && item.start >= was.start && (carry || item.end <= now);
  const breakAfterHard = (item: TimerCalendarItem) =>
    item.kind === 'fixed'
      ? breaks.eventBreakMinutes ?? 0
      : item.kind === 'commute'
      ? 0
      : breakAfter(minutesBetween(item.start, item.end), breaks);
  // The first place from `start` that holds the dragged session whole, past whatever it would sit on that never moves.
  // Where the student put it stands when nothing fixed is there, awake or not; once Shovel moves it, only into awake
  // time, whole (a long session dropped into a day of classes goes on to the next morning, not into the night).
  const snap = (length: number, hard: TimerCalendarItem[]) => {
    let at = start;
    let moved = false;
    if (stretchedFrom === undefined) {
      for (let guard = 0; guard < hard.length + context.awake.length + 2; guard++) {
        const end = at + length;
        const hits = hard.filter(item => item.start < end && item.end > at);
        if (hits.length) {
          const last = hits.reduce((latest, item) => (item.end > latest.end ? item : latest));
          at = last.end + breakAfterHard(last) * MINUTE;
          moved = true;
          continue;
        }
        if (!moved) break;
        const awake = context.awake.find(window => window.end > at);
        if (!awake) {
          at = Infinity;
          break;
        }
        if (awake.start > at) {
          at = awake.start;
          continue;
        }
        if (end <= awake.end) break;
        const next = context.awake.find(window => window.start >= awake.end);
        if (!next) {
          at = Infinity;
          break;
        }
        at = next.start;
      }
    }
    return { at };
  };

  // The dragged session `length` minutes long, with these sessions held: its place, and what the bump does.
  const attempt = (length: number, held: Set<string>) => {
    const hard = others.filter(item => stays(item) || held.has(item.id));
    const place = snap(length * MINUTE, hard);
    const end = place.at + length * MINUTE;
    const movable = (item: TimerCalendarItem) => !stays(item) && !held.has(item.id);
    // Jump, dropped from now on: the missed sessions it passed don't ride on in front of it. They start again from now,
    // in order, bumping what's in their way, and it jumps over them to where it was dropped (Petr, 2026-10-29: "they
    // only get bumped until they go past now, then we jump over them"). Over the past, and with Bump, they stay in front.
    const rescue =
      !carry && place.at >= now && others.some(item => movable(item) && item.end <= now && passedOver(item, place.at));
    const from = rescue ? now : place.at;
    // From `from` on, every session is in the chain's way (when it's the drop, those already reach past it).
    const moving = (item: TimerCalendarItem) =>
      movable(item) && (item.end > place.at || item.start >= from || passedOver(item, place.at));
    const queue = others.filter(moving).sort((a, b) => a.start - b.start);
    const taken: Taken[] = [
      ...others
        .filter(item => !moving(item))
        .map(item => ({ start: item.start, end: item.end, kind: item.kind, minutes: minutesBetween(item.start, item.end) })),
      { start: place.at, end, kind: 'started', minutes: length }
    ];
    const result = bumpAfter({ queue, taken, from, now, breaks, context, endsWhenClear: end });
    return { place, end, result, lost: lostIn(result) };
  };

  const settle = (length: number) =>
    holdForDueDates(
      held => attempt(length, held),
      others.filter(item => dueWithin(item, now, context.horizonEnd)),
      tried => tried.place.at + length * MINUTE > context.horizonEnd
    );
  const fitsTerm = (tried: ReturnType<typeof settle>) => !tried.lost.length && tried.end <= context.horizonEnd;

  const whole = Math.max(1, Math.round(minutes));
  let length = whole;
  let tried = settle(length);
  if (!fitsTerm(tried)) {
    // The dragged session pays: the longest that fits, in 5-minute steps.
    const shortest = stretchedFrom !== undefined ? Math.min(whole, Math.round(stretchedFrom)) : PIECE_FLOOR_MINUTES;
    let found: { length: number; tried: ReturnType<typeof settle> } | undefined;
    for (let shorter = whole - 5; !wholeOnly && shorter >= shortest && !found; shorter -= 5) {
      const option = settle(shorter);
      if (fitsTerm(option)) found = { length: shorter, tried: option };
    }
    // The shortest itself, when the steps passed it (an hour stretched from 47 minutes, a 17-minute session).
    if (!wholeOnly && !found && shortest < whole && (whole - shortest) % 5) {
      const option = settle(shortest);
      if (fitsTerm(option)) found = { length: shortest, tried: option };
    }
    if (!found) {
      return { kind: 'refused', start, end: start + whole * MINUTE, minutes: whole, moves: [], creates: [], held: [] };
    }
    length = found.length;
    tried = found.tried;
  }

  const { place, end, result, held } = tried;
  const shortenedBy = whole - length;
  return {
    kind: shortenedBy > 0 ? 'shortened' : result.moves.length ? 'bumps' : 'fits',
    start: place.at,
    end,
    minutes: length,
    ...(shortenedBy > 0 ? { shortenedBy } : {}),
    moves: result.moves,
    creates: result.creates,
    held: Array.from(held),
    ...daysReached(result, context)
  };
};

/** The first and the last day a plan's sessions go to (each day's start), for the tag's words. */
const daysReached = (
  result: Pick<BumpResult, 'moves' | 'creates'>,
  context: BumpContext
): Pick<BumpersPlan, 'firstDay' | 'lastDay'> => {
  const starts = [...result.moves.map(move => move.start), ...result.creates.map(piece => piece.start)];
  if (!starts.length) return {};
  const dayStartOf = (at: number) => {
    const dayEnd = context.dayEnds?.find(ends => ends > at);
    if (dayEnd !== undefined) return dayEnd - DAY;
    return context.dayEnd + Math.floor((at - context.dayEnd) / DAY) * DAY;
  };
  return { firstDay: dayStartOf(Math.min(...starts)), lastDay: dayStartOf(Math.max(...starts)) };
};

/**
 * Bumpers dragged up (Petr, 2026-10-25: "if I move down or up, it's the same functionality"): a session dropped earlier
 * than it was (with Jump, on the same day; bumpers.ts) pushes the sessions it reaches into up ahead of it, in order,
 * round classes, events and commutes, as far as it must; dropped wholly above one, it leaves that one where it is and
 * slots in. Worked out by planBumpers on time run backwards, so it pushes up exactly as a drop pushes down, breaks and
 * splits included, never into the past (now is where its time ends). Due dates don't hold anything back, as earlier is
 * never late. With no room before now for all of it (the dragged session would have to shrink), it bumps later instead,
 * as before.
 */
export const planBumpersEarlier = (input: Parameters<typeof planBumpers>[0]): BumpersPlan => {
  const { now, start, minutes, items, breaks, context, id } = input;
  const flip = <T extends Interval>(item: T): T => ({ ...item, start: -item.end, end: -item.start });
  // Each day's start becomes the end of that day backwards, today's included.
  const dayEnds = [context.dayEnd - DAY, ...(context.dayEnds ?? [context.dayEnd])].map(at => -at).sort((a, b) => a - b);
  const back = planBumpers({
    now: -context.horizonEnd,
    start: -(start + Math.round(minutes) * MINUTE),
    minutes,
    items: items.map(item => ({ ...flip(item), dueDate: undefined })),
    breaks,
    context: {
      awake: context.awake
        .filter(window => window.end > now)
        .map(window => flip({ start: Math.max(window.start, now), end: window.end }))
        .reverse(),
      dayEnd: -(context.dayEnd - DAY),
      horizonEnd: -now,
      minBlock: context.minBlock,
      dayEnds
    },
    id,
    // Bump dragged up: the sessions it passes on its way up travel with it, ahead of it.
    carry: input.carry,
    // Not whole before now, it bumps later instead: no use trying it shorter backwards.
    wholeOnly: true
  });
  if (back.kind === 'shortened' || back.kind === 'refused') return planBumpers(input);
  const startOf = new Map(items.map(item => [item.id, item.start]));
  const moves = back.moves.map(move => {
    const moved = flip(move);
    const was = startOf.get(move.id);
    return { ...moved, by: was !== undefined ? Math.round((moved.start - was) / MINUTE) : moved.by };
  });
  const creates = back.creates.map(flip);
  return {
    kind: back.kind,
    start: -back.end,
    end: -back.start,
    minutes: back.minutes,
    moves,
    creates,
    held: [],
    ...daysReached({ moves, creates }, context)
  };
};

/**
 * "Shorten the next one": this session whole, the sessions before the next fixed thing pushed with the breaks shrunk,
 * and the first of them shortened so they all end before it, no shorter than 5 minutes; its lost time back to its task.
 * None when they fit anyway.
 */
export const shortenNextOption = ({
  now,
  plannedMinutes,
  items
}: Pick<StartInput, 'now' | 'plannedMinutes' | 'items'>): TimerStartOption | undefined => {
  const plan = Math.max(0, Math.round(plannedMinutes));
  const fixed = firstFixedAfter(items, now);
  if (!fixed) return undefined;
  const sessions = items
    .filter(item => item.kind === 'session' && item.movable !== false && item.start >= now && item.start < fixed.start)
    .sort((a, b) => a.start - b.start);
  if (!sessions.length) return undefined;
  const tight = layOut(
    now + plan * MINUTE,
    sessions,
    sessions.map(() => 0)
  );
  const overflow = tight.end + leastBeforeFixed(fixed) * MINUTE - fixed.start;
  if (overflow <= 0) return undefined;
  const [first, ...rest] = tight.placed;
  const firstEnd = first.end - overflow;
  if (firstEnd - first.start < PIECE_FLOOR_MINUTES * MINUTE) return undefined;
  const placed = [
    { ...first, end: firstEnd },
    ...rest.map(item => {
      const start = Math.max(item.session.start, item.start - overflow);
      return { ...item, start, end: start + (item.end - item.start) };
    })
  ];
  const moves: SessionMove[] = placed
    .filter(({ session, start, end }) => start !== session.start || end !== session.end)
    .map(({ session, start, end }) => {
      const shortenedBy = Math.round((session.end - session.start - (end - start)) / MINUTE);
      return {
        id: session.id,
        start,
        end,
        title: session.title,
        by: Math.round((start - session.start) / MINUTE),
        ...(shortenedBy > 0 ? { shortenedBy } : {})
      };
    });
  return {
    kind: 'shortensNext',
    way: 'shortenNext',
    minutes: plan,
    moves,
    givesBack: 0
  };
};

/** "Stop when the next session starts": nothing moves. None when the next session is past the plan anyway. */
const untilSessionOption = ({
  now,
  plannedMinutes,
  items
}: Pick<StartInput, 'now' | 'plannedMinutes' | 'items'>): TimerStartOption | undefined => {
  const plan = Math.max(0, Math.round(plannedMinutes));
  const next = items.filter(item => item.kind === 'session' && item.start >= now).sort((a, b) => a.start - b.start)[0];
  if (!next) return undefined;
  const minutes = Math.floor((next.start - now) / MINUTE);
  return minutes >= 1 && minutes < plan
    ? {
        kind: 'stopsAtSession',
        way: 'untilSession',
        minutes,
        moves: [],
        givesBack: plan - minutes
      }
    : undefined;
};

const wayOf = (option: TimerStartOption): StartWay =>
  option.kind === 'stopsAtBedtime'
    ? 'untilBedtime'
    : option.kind === 'stopsAtSession'
    ? 'untilSession'
    : option.kind === 'stopsFor' && option.moves.length
    ? 'shortenThis'
    : option.kind === 'shortensNext'
    ? 'shortenNext'
    : 'bump';

/**
 * The ways ▶ can go, the default first (the student's "When I start late" pick when it's one of them, else bumping the
 * day): bumping the day down, stopping at bedtime when it would run past it, shortening this one so the next is only
 * pushed, shortening the next one, and stopping when the next session starts. Just the one when nothing is in the way. Without awake times to go by (the calendar's
 * data not loaded), the plain late-start rules (planTimerStart).
 */
export const planSmartStart = (input: StartInput & { prefer?: StartWay }): TimerStartOption[] => {
  const { prefer, ...start } = input;
  const legacy = planTimerStart({
    now: start.now,
    plannedMinutes: start.plannedMinutes,
    items: start.items,
    breaks: start.breaks
  });
  let options: TimerStartOption[];
  if (!start.context.awake.length) {
    options = [legacy.chosen, legacy.other]
      .filter((option): option is TimerStartOption => !!option && option.kind !== 'full')
      .map(option => ({ ...option, way: wayOf(option) }));
  } else {
    const bump = planBump(start);
    options = [bump];
    // Running through an Activity still ahead: Change offers stopping for it all the same.
    if (bump.runsThrough && bump.runsThrough.at >= start.now) {
      const stopping = planBump({ ...start, stopBefore: bump.runsThrough.id });
      if (stopping.minutes < bump.minutes) options.push({ ...stopping, way: 'untilActivity' });
    }
    // Running past bedtime: said, never cut short on its own (Petr, 2026-10-19: ▶ is the student's call); stopping
    // there is a Change pick.
    const bedtime = bedtimeAt(start.now, start.context.awake);
    const untilBedtime = bedtime !== undefined ? Math.floor((bedtime - start.now) / MINUTE) : 0;
    if (bedtime !== undefined && untilBedtime >= SHORTEST_COUNTDOWN_MINUTES && untilBedtime < bump.minutes) {
      const atBedtime = planBump({ ...start, stopAt: bedtime });
      options.push({ ...atBedtime, kind: 'stopsAtBedtime', way: 'untilBedtime', bedtime, stopFor: undefined });
    }
    if (bump.kind !== 'fits') {
      // Stopping for the class and giving the rest back to the task: offered when the default places the rest; not for
      // an Activity the default runs through, which Change offers to stop for already.
      if (
        legacy.chosen.kind === 'stopsFor' &&
        (legacy.chosen.moves.length || bump.rest) &&
        (!bump.runsThrough || legacy.chosen.stopFor?.at !== bump.runsThrough.at)
      )
        options.push({ ...legacy.chosen, way: 'shortenThis' });
      const next = shortenNextOption(start);
      if (next) options.push(next);
      const until = untilSessionOption(start);
      if (until) options.push(until);
    }
    if (bedtime !== undefined)
      options = options.map(option => {
        const over = Math.round((start.now + option.minutes * MINUTE - bedtime) / MINUTE);
        return over > 0 && start.now < bedtime ? { ...option, pastBedtime: over } : option;
      });
  }
  const seen = new Set<StartWay>();
  const unique = options.filter(option => {
    const way = option.way || wayOf(option);
    if (seen.has(way)) return false;
    seen.add(way);
    return true;
  });
  const picked = unique.findIndex(option => option.way === prefer);
  return picked > 0 ? [unique[picked], ...unique.filter((_, index) => index !== picked)] : unique;
};
